import {
  AgentApproval,
  AgentMetrics,
  RejectionReasonTag,
} from '@models/approval';
import { db, FirebaseCallable, functions, httpsCallable } from '@util/firebase';
import {
  collection,
  doc,
  limit as firestoreLimit,
  onSnapshot,
  orderBy,
  query,
  where,
} from 'firebase/firestore';
import { LinearIssue } from 'models/linear';

// --- Approval CRUD via httpsCallable ---

export const getApprovals = async ({
  limit = 20,
  startAfter,
}: {
  limit?: number;
  startAfter?: string;
}) => {
  const fn = httpsCallable<
    { limit?: number; start_after?: string },
    { approvals: AgentApproval[] }
  >(functions, FirebaseCallable.getApprovals);
  const result = await fn({ limit, start_after: startAfter });
  return result.data.approvals;
};

export type ApprovalTimeSeriesStatus =
  | 'approved'
  | 'edited'
  | 'rejected'
  | 'auto_approved';

export type ApprovalTimeSeries = {
  buckets: string[];
  series: Record<ApprovalTimeSeriesStatus, number[]>;
};

export const getApprovalTimeSeries = async ({
  startMs,
  endMs,
  bucket = 'week',
}: {
  startMs: number;
  endMs: number;
  bucket?: 'day' | 'week';
}) => {
  const fn = httpsCallable<
    { start_ms: number; end_ms: number; bucket: 'day' | 'week' },
    ApprovalTimeSeries
  >(functions, FirebaseCallable.getApprovalTimeSeries);
  const result = await fn({ start_ms: startMs, end_ms: endMs, bucket });
  return result.data;
};

export const approveMessage = async ({
  approvalId,
  approvedActionIds,
  saveAsExample,
}: {
  approvalId: string;
  approvedActionIds?: string[];
  saveAsExample?: boolean;
}) => {
  const fn = httpsCallable<
    {
      approval_id: string;
      action: 'approve';
      approved_action_ids?: string[];
      save_as_example?: boolean;
    },
    { success: boolean }
  >(functions, FirebaseCallable.approveMessage);
  await fn({
    approval_id: approvalId,
    action: 'approve',
    approved_action_ids: approvedActionIds,
    save_as_example: saveAsExample,
  });
};

export const rejectMessage = async ({
  approvalId,
  reason,
  reasonTags,
}: {
  approvalId: string;
  reason?: string;
  reasonTags?: RejectionReasonTag[];
}) => {
  const fn = httpsCallable<
    {
      approval_id: string;
      action: 'reject';
      reason?: string;
      reason_tags?: RejectionReasonTag[];
    },
    { success: boolean }
  >(functions, FirebaseCallable.approveMessage);
  await fn({
    approval_id: approvalId,
    action: 'reject',
    reason,
    reason_tags: reasonTags,
  });
};

export const editAndApproveMessage = async ({
  approvalId,
  editedMessage,
  approvedActionIds,
  saveAsExample,
}: {
  approvalId: string;
  editedMessage: string;
  approvedActionIds?: string[];
  saveAsExample?: boolean;
}) => {
  const fn = httpsCallable<
    {
      approval_id: string;
      action: 'edit';
      edited_message: string;
      approved_action_ids?: string[];
      save_as_example?: boolean;
    },
    { success: boolean }
  >(functions, FirebaseCallable.approveMessage);
  await fn({
    approval_id: approvalId,
    action: 'edit',
    edited_message: editedMessage,
    approved_action_ids: approvedActionIds,
    save_as_example: saveAsExample,
  });
};

export const retryMessage = async ({
  approvalId,
  additionalInstructions,
}: {
  approvalId: string;
  additionalInstructions: string;
}) => {
  const fn = httpsCallable<
    {
      approval_id: string;
      action: 'retry';
      additional_instructions: string;
    },
    {
      success: boolean;
      message?: string;
      approval_id?: string;
      actions?: import('@models/approval').ProposedAction[];
      task_id?: string;
      internal?: boolean;
    }
  >(functions, FirebaseCallable.approveMessage);
  const result = await fn({
    approval_id: approvalId,
    action: 'retry',
    additional_instructions: additionalInstructions,
  });
  return result.data;
};

// --- Global Settings (Kill Switch) ---

export interface GlobalConfig {
  bot_enabled: boolean;
  updated_by: string;
  updated_at: number;
}

export const getGlobalSettings = async (): Promise<GlobalConfig> => {
  const fn = httpsCallable<void, GlobalConfig>(
    functions,
    FirebaseCallable.getGlobalSettings
  );
  const result = await fn();
  return result.data;
};

export const updateGlobalSettings = async ({
  botEnabled,
}: {
  botEnabled: boolean;
}) => {
  const fn = httpsCallable<{ bot_enabled: boolean }, { success: boolean }>(
    functions,
    FirebaseCallable.updateGlobalSettings
  );
  await fn({ bot_enabled: botEnabled });
};

// --- Task Settings ---

export interface TaskToggle {
  enabled: boolean;
  updated_by: string;
  updated_at: number;
}

export interface TaskSettingsResponse {
  tasks: { id: string; name: string }[];
  toggles: Record<string, TaskToggle>;
}

export const getTaskSettings = async (): Promise<TaskSettingsResponse> => {
  const fn = httpsCallable<void, TaskSettingsResponse>(
    functions,
    FirebaseCallable.getTaskSettings
  );
  const result = await fn();
  return result.data;
};

export const updateTaskSettings = async ({
  taskId,
  enabled,
}: {
  taskId: string;
  enabled: boolean;
}) => {
  const fn = httpsCallable<
    { task_id: string; enabled: boolean },
    { success: boolean }
  >(functions, FirebaseCallable.updateTaskSettings);
  await fn({ task_id: taskId, enabled });
};

// --- Delay Settings ---

export interface DelayConfig {
  min_delay_seconds: number;
  max_delay_seconds: number;
  updated_by: string;
  updated_at: number;
}

export const getDelaySettings = async (): Promise<DelayConfig> => {
  const fn = httpsCallable<void, DelayConfig>(
    functions,
    FirebaseCallable.getDelaySettings
  );
  const result = await fn();
  return result.data;
};

export const updateDelaySettings = async ({
  minDelaySeconds,
  maxDelaySeconds,
}: {
  minDelaySeconds: number;
  maxDelaySeconds: number;
}) => {
  const fn = httpsCallable<
    { min_delay_seconds: number; max_delay_seconds: number },
    { success: boolean }
  >(functions, FirebaseCallable.updateDelaySettings);
  await fn({
    min_delay_seconds: minDelaySeconds,
    max_delay_seconds: maxDelaySeconds,
  });
};

// --- Real-time pending approval listener for a specific chat ---

export const subscribeToPendingApproval = (
  chatId: string,
  callback: (approval: AgentApproval | null) => void
) => {
  const q = query(
    collection(db, 'agent_approvals'),
    where('chat_id', '==', chatId),
    where('status', '==', 'pending'),
    orderBy('created', 'desc'),
    firestoreLimit(1)
  );
  return onSnapshot(q, (snapshot) => {
    if (snapshot.empty) {
      callback(null);
    } else {
      const doc = snapshot.docs[0];
      callback({ id: doc.id, ...doc.data() } as AgentApproval);
    }
  });
};

// --- Real-time metrics listener ---

export const subscribeToAgentMetrics = (
  callback: (metrics: AgentMetrics) => void
) => {
  const ref = doc(db, 'agent_metrics', 'global');
  return onSnapshot(ref, (snap) => {
    if (snap.exists()) {
      callback(snap.data() as AgentMetrics);
    } else {
      callback({
        pending_count: 0,
        approved_count: 0,
        rejected_count: 0,
        edited_count: 0,
        auto_approved_count: 0,
      });
    }
  });
};

export const getLinearIssueUrl = async (chatId: string) => {
  const fn = httpsCallable<
    { chat_id: string },
    { issue_url: string; status: 'exists' } | { status: 'not_found' }
  >(functions, FirebaseCallable.getLinearIssue);
  const result = await fn({ chat_id: chatId });
  return result.data.status === 'exists' ? result.data.issue_url : null;
};

export const createLinearIssue = async (issue: LinearIssue) => {
  const fn = httpsCallable<
    { issue: LinearIssue },
    { issue_url: string; status: 'exists' | 'created' }
  >(functions, FirebaseCallable.createLinearIssue);
  const result = await fn({ issue });
  return result.data.issue_url;
};
